<template>
  <!-- Mobile Overlay -->
  <Transition name="overlay">
    <div
      v-if="isMobileMenuOpen"
      class="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
      @click="handleMobileClose"
      @touchstart="handleMobileClose"
      aria-hidden="true"
    ></div>
  </Transition>

  <!-- Toggle button (Desktop - Outside sidebar, vertically centered) -->
  <Transition name="slide-right">
    <button
      v-if="true"
      @click="handleDesktopToggle"
      @keydown.enter="handleDesktopToggle"
      @keydown.space.prevent="handleDesktopToggle"
      class="hidden lg:flex fixed top-1/2 -translate-y-1/2 z-50 items-center justify-center w-6 h-16 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-r-lg shadow-lg text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 hover:shadow-xl group focus:outline-none focus:ring-2 focus:ring-blue-500"
      :class="isSidebarOpen ? 'left-64' : 'left-20'"
      :title="isSidebarOpen ? t('sidebar.collapse') : t('sidebar.expand')"
      :aria-label="isSidebarOpen ? t('sidebar.collapse') : t('sidebar.expand')"
      tabindex="0"
    >
      <svg
        class="w-4 h-4 transition-transform duration-300"
        :class="{ 'rotate-180': !isSidebarOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 19l-7-7 7-7"
        />
      </svg>
    </button>
  </Transition>

  <!-- Sidebar -->
  <aside
    ref="sidebarRef"
    :class="sidebarClasses"
    role="navigation"
    aria-label="Main navigation"
    @keydown="handleKeyNavigation"
  >
    <!-- Loading Skeleton -->
    <div v-if="isLoading" class="animate-pulse p-4">
      <div class="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
      <div class="space-y-2">
        <div v-for="i in 6" :key="i" class="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    </div>

    <!-- Main Content -->
    <template v-else>
      <!-- Header Section -->
      <div class="h-12 sm:h-14 flex items-center px-3 sm:px-4">
        <div class="flex items-center gap-2 font-bold text-base sm:text-lg text-slate-800 dark:text-gray-100">
          <span class="text-blue-600 dark:text-blue-500 flex items-center justify-center">
            <img src="/logo_alkara.png" alt="Alkara Logo" class="w-6 h-6 sm:w-7 sm:h-7 object-contain">
          </span>
          <Transition name="slide-fade-x">
            <span v-if="isSidebarOpen" class="tracking-tight text-sm sm:text-base">ERP Maintenance</span>
          </Transition>
        </div>
      </div>

      <!-- Enhanced Breadcrumb (when expanded) -->
      <Transition name="slide-fade">
        <div
          v-if="isSidebarOpen && breadcrumbs.length > 1"
          class="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
        >
          <nav class="flex items-center space-x-1 text-xs" aria-label="Breadcrumb">
            <template v-for="(crumb, index) in breadcrumbs" :key="crumb.path">
              <router-link
                v-if="index < breadcrumbs.length - 1"
                :to="crumb.path"
                class="text-gray-600 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded px-1"
                :aria-label="`Navigate to ${t(crumb.labelKey)}`"
              >
                {{ t(crumb.labelKey) }}
              </router-link>
              <span
                v-else
                class="font-semibold text-gray-800 dark:text-gray-200"
                aria-current="page"
              >
                {{ t(crumb.labelKey) }}
              </span>
              <svg
                v-if="index < breadcrumbs.length - 1"
                class="w-3 h-3 text-gray-500 dark:text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </template>
          </nav>
        </div>
      </Transition>

      <!-- Recent/Favorites (when expanded) -->
      <Transition name="slide-fade">
        <div
          v-if="isSidebarOpen && (recentItems.length > 0 || favoriteItems.length > 0)"
          class="px-4 py-3 border-b border-gray-100 dark:border-gray-800"
        >
          <!-- Favorites -->
          <div v-if="favoriteItems.length > 0" class="mb-3">
            <h4
              class="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-2"
            >
              {{ t('sidebar.favorites') }}
            </h4>
            <div class="flex flex-wrap gap-1">
              <router-link
                v-for="item in favoriteItems"
                :key="item.path"
                :to="item.path"
                class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                @click="handleNavigation(() => router.push(item.path), item)"
              >
                <svg
                  class="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  v-html="item.iconPath"
                />
                {{ t(item.labelKey) }}
              </router-link>
            </div>
          </div>

          <!-- Recent -->
          <div v-if="recentItems.length > 0">
            <div class="flex items-center justify-between mb-2">
              <h4
                class="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300"
              >
                {{ t('sidebar.recent') }}
              </h4>
              <button
                v-if="recentItems.length > 0"
                @click="clearRecent"
                class="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 rounded px-1"
                :title="t('sidebar.clearRecent')"
                :aria-label="t('sidebar.clearRecent')"
              >
                {{ t('sidebar.clearRecent') }}
              </button>
            </div>
            <div class="flex flex-wrap gap-1">
              <div
                v-for="item in recentItems"
                :key="item.path"
                class="group relative inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-all duration-200"
              >
                <router-link
                  :to="item.path"
                  class="inline-flex items-center gap-1 hover:text-teal-600 dark:hover:text-teal-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded"
                  @click="handleNavigation(() => router.push(item.path), item)"
                >
                  <svg
                    class="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    v-html="item.iconPath"
                  />
                  {{ t(item.labelKey) }}
                </router-link>
                <button
                  @click.stop="removeFromRecent(item)"
                  class="ml-1 opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                  :title="t('sidebar.removeRecent')"
                  :aria-label="t('sidebar.removeRecent')"
                >
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Navigation Menu -->
      <nav
        class="flex-1 overflow-y-auto px-4 py-4 space-y-6 custom-scrollbar"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <template v-for="(group, groupIndex) in filteredMenuGroups" :key="groupIndex">
          <div v-if="!isLazyLoading || visibleItems.has(`group-${groupIndex}`)">
            <!-- Group Title -->
            <Transition name="fade">
              <div
                v-if="isSidebarOpen || group.items.some((item) => isRouteActive(item))"
                class="flex items-center justify-between mb-2"
                :data-group-id="`group-${groupIndex}`"
              >
              <h3 :class="groupTitleClasses">
                {{ t(group.titleKey) }}
              </h3>
              <button
                v-if="isSidebarOpen && group.collapsible"
                @click="toggleGroupCollapse(groupIndex)"
                class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                :aria-label="
                  collapsedGroups.has(groupIndex)
                    ? t('sidebar.expandGroup')
                    : t('sidebar.collapseGroup')
                "
              >
                <svg
                  class="w-4 h-4 transition-transform duration-200"
                  :class="{ 'rotate-180': collapsedGroups.has(groupIndex) }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          </Transition>

          <!-- Menu Items -->
          <Transition name="slide-fade">
            <div v-if="!collapsedGroups.has(groupIndex)" class="space-y-1">
              <template v-for="item in group.items" :key="item.path">
                <!-- Main Menu Item -->
                <router-link
                  :to="item.path"
                  custom
                  v-slot="{ href, navigate, isActive, isExactActive }"
                >
                  <a
                    :href="href"
                    @click="handleNavigation(navigate, item)"
                    :class="[
                      'group relative flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 font-medium text-sm',
                      (item.exact ? isExactActive : isActive)
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-800 hover:text-blue-600 dark:hover:text-blue-400',
                    ]"
                  >
                    <!-- Icon -->
                    <div class="relative flex-shrink-0">
                      <svg
                        class="w-[20px] h-[20px] transition-all duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        v-html="item.iconPath"
                      />
                    </div>

                    <!-- Label -->
                    <Transition name="slide-fade-x">
                      <span v-if="isSidebarOpen" class="flex-1 min-w-0">
                        {{ t(item.labelKey) }}
                      </span>
                    </Transition>

                    <!-- Badge (Modernize style) -->
                    <Transition name="fade">
                      <span
                        v-if="item.badge && isSidebarOpen"
                        class="flex-shrink-0 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 py-0.5 px-2 rounded text-[10px] font-semibold"
                      >
                        {{ item.badge }}
                      </span>
                    </Transition>

                    <!-- Submenu indicator -->
                    <svg
                      v-if="isSidebarOpen && item.children && item.children.length > 0"
                      class="flex-shrink-0 w-4 h-4 text-gray-400 transition-transform duration-200"
                      :class="{ 'rotate-90': expandedItems.has(item.path) }"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>

                    <!-- Favorite toggle (absolutely positioned) -->
                    <button
                      v-if="isSidebarOpen"
                      @click.stop.prevent="toggleFavorite(item)"
                      class="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-yellow-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded"
                      :aria-label="
                        isFavorite(item) ? t('sidebar.removeFavorite') : t('sidebar.addFavorite')
                      "
                    >
                      <svg
                        class="w-4 h-4"
                        :class="{ 'text-yellow-500 fill-current': isFavorite(item) }"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                        />
                      </svg>
                    </button>

                    <!-- Arrow indicator for active items when collapsed -->
                    <Transition name="fade">
                      <span
                        v-if="!isSidebarOpen && isRouteActive(item)"
                        class="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full"
                      />
                    </Transition>
                  </a>
                </router-link>

                <!-- Submenu Items -->
                <Transition name="slide-fade">
                  <div
                    v-if="isSidebarOpen && item.children && expandedItems.has(item.path)"
                    class="ml-6 mt-1 space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-3"
                  >
                    <router-link
                      v-for="child in item.children"
                      :key="child.path"
                      :to="child.path"
                      custom
                      v-slot="{ href, navigate, isActive, isExactActive }"
                    >
                      <a
                        :href="href"
                        @click="handleNavigation(navigate, child)"
                        :class="getSubLinkClasses(child, isActive, isExactActive)"
                        :aria-current="isRouteActive(child) ? 'page' : undefined"
                      >
                        <svg
                          class="w-4 h-4 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          v-html="child.iconPath"
                        />
                        <span class="flex-1 min-w-0">{{ t(child.labelKey) }}</span>
                      </a>
                    </router-link>
                  </div>
                </Transition>
              </template>
            </div>
          </Transition>
          </div>
        </template>

        <!-- No results message -->
        <Transition name="fade">
          <div
            v-if="searchQuery && filteredMenuGroups.length === 0"
            class="text-center py-8 text-gray-600 dark:text-gray-300"
          >
            <svg
              class="w-12 h-12 mx-auto mb-2 opacity-50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p class="text-sm font-medium">{{ t('sidebar.noResults') }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Try different keywords or check spelling
            </p>
            <button
              @click="clearSearch"
              class="mt-3 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 hover:underline focus:outline-none focus:ring-2 focus:ring-teal-500 rounded px-2 py-1"
            >
              {{ t('sidebar.clearSearch') }}
            </button>
          </div>
        </Transition>
      </nav>

      <!-- Footer / User Profile -->
      <div class="p-4 mt-auto">
        <!-- User Profile -->
        <div
          class="bg-blue-50 dark:bg-gray-800 rounded-lg flex items-center gap-3 p-3.5 transition-colors duration-200"
        >
          <Transition name="slide-fade-x">
            <div v-if="isSidebarOpen" class="flex-1 min-w-0">
              <p class="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">Alkara Solution</p>
              <p class="text-xs text-gray-500 dark:text-gray-400 truncate">ERP Maintenance</p>
            </div>
          </Transition>

          <!-- Theme Toggle Button (replacing logout) -->
          <button
            @click="toggleTheme"
            class="flex items-center justify-center w-9 h-9 text-gray-400 hover:text-yellow-500 dark:hover:text-yellow-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            :title="isDark ? t('sidebar.theme.switchToLight') : t('sidebar.theme.switchToDark')"
            :aria-label="isDark ? t('sidebar.theme.switchToLight') : t('sidebar.theme.switchToDark')"
          >
            <!-- Sun Icon (Light Mode) -->
            <svg
              v-if="!isDark"
              class="w-5 h-5 transition-transform duration-300 hover:rotate-180"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <!-- Moon Icon (Dark Mode) -->
            <svg
              v-else
              class="w-5 h-5 transition-transform duration-300 hover:rotate-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          </button>
        </div>
      </div>
    </template>
  </aside>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { menuGroups, type MenuItem, type MenuGroup } from '@/config/menuConfig'
import { useSidebarHelpers, useSidebarPersistence } from '@/composables/useSidebarHelpers'
import { useThemeStore } from '@/stores/theme'
import { usePermissions } from '@/composables/usePermissions'

// Props & Emits
const props = defineProps<{
  isSidebarOpen: boolean
  isMobileMenuOpen: boolean
}>()

const emit = defineEmits<{
  'toggle-sidebar': []
  'toggle-mobile-menu': [value: boolean]
}>()

// Composables
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { routeExists, filterValidMenuItems, isRouteActive, findMenuItemByPath, fuzzyMatch } =
  useSidebarHelpers(router)
const persistence = useSidebarPersistence()
const themeStore = useThemeStore()
const { canAccessMenu } = usePermissions()

// Refs
const sidebarRef = ref<HTMLElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)
const searchQuery = ref('')
const isLoading = ref(false)
const showUserMenu = ref(false)
const collapsedGroups = ref(new Set<number>())
const expandedItems = ref(new Set<string>())
const favoriteItems = ref<MenuItem[]>([])
const recentItems = ref<MenuItem[]>([])
const currentFocusIndex = ref(-1)
const visibleItems = ref<Set<string>>(new Set())
const touchStartX = ref(0)
const touchStartY = ref(0)
const isLazyLoading = ref(false)

// Quick Action and User Action Interfaces
interface QuickAction {
  key: string
  labelKey: string
  iconPath: string
  action: () => void
}

interface UserAction {
  key: string
  labelKey: string
  iconPath: string
  action: () => void
}

// Computed
const userInitials = computed(() => {
  const name = t('sidebar.profile.name')
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
})

const sidebarClasses = computed(() => [
  'fixed lg:sticky top-0 left-0 h-screen bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col z-50 transform transition-all duration-300 ease-in-out',
  props.isSidebarOpen ? 'w-64' : 'w-20',
  props.isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
])

const groupTitleClasses = computed(() => [
  'text-xs font-bold uppercase tracking-wider transition-all duration-200',
  props.isSidebarOpen
    ? 'text-gray-400 dark:text-gray-500 px-2'
    : 'text-center text-gray-500 dark:text-gray-500 text-[8px] px-1',
])

const logoutButtonClasses = computed(() => [
  'flex items-center w-full mt-3 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500',
  !props.isSidebarOpen && 'justify-center',
])

// Breadcrumbs
const breadcrumbs = computed(() => {
  const pathSegments = route.path.split('/').filter(Boolean)
  const crumbs = [{ labelKey: 'sidebar.menu.dashboard', path: '/' }]

  let currentPath = ''
  for (const segment of pathSegments) {
    currentPath += `/${segment}`
    const menuItem = findMenuItemByPath(currentPath, menuGroups)
    if (menuItem) {
      crumbs.push({ labelKey: menuItem.labelKey, path: currentPath })
    }
  }

  return crumbs
})

// Quick Actions - filtered to only show routes that exist
const quickActions = computed<QuickAction[]>(() => {
  const actions: QuickAction[] = []

  if (routeExists('/work-orders/new')) {
    actions.push({
      key: 'newWorkOrder',
      labelKey: 'sidebar.quickActions.newWorkOrder',
      iconPath:
        '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>',
      action: () => router.push('/work-orders/new'),
    })
  }

  if (routeExists('/maintenance-requests/new')) {
    actions.push({
      key: 'newRequest',
      labelKey: 'sidebar.quickActions.newRequest',
      iconPath:
        '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>',
      action: () => router.push('/maintenance-requests/new'),
    })
  }

  return actions
})

// User Menu Actions - filtered to only show routes that exist
const userMenuActions = computed<UserAction[]>(() => {
  const actions: UserAction[] = []

  if (routeExists('/profile')) {
    actions.push({
      key: 'profile',
      labelKey: 'sidebar.userMenu.profile',
      iconPath:
        '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>',
      action: () => router.push('/profile'),
    })
  }

  if (routeExists('/settings')) {
    actions.push({
      key: 'settings',
      labelKey: 'sidebar.userMenu.settings',
      iconPath:
        '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.527.288 1.054.455 1.571.468z"/>',
      action: () => router.push('/settings'),
    })
  }

  if (routeExists('/help-and-flow')) {
    actions.push({
      key: 'help',
      labelKey: 'sidebar.userMenu.help',
      iconPath:
        '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>',
      action: () => router.push('/help-and-flow'),
    })
  }

  return actions
})
const filteredMenuGroups = computed(() => {
  const validGroups = menuGroups
    .map((group) => ({
      ...group,
      items: filterValidMenuItems(group.items).filter((item) => canAccessMenu(item.path)),
    }))
    .filter((group) => group.items.length > 0)
    .sort((a, b) => (a.priority || 999) - (b.priority || 999))

  if (!searchQuery.value.trim()) {
    return validGroups
  }

  const query = searchQuery.value.toLowerCase()

  // Fuzzy matching helper
  const fuzzyMatch = (text: string, pattern: string): boolean => {
    if (text.includes(pattern)) return true
    const textLower = text.toLowerCase()
    const patternLower = pattern.toLowerCase()
    let patternIndex = 0
    for (let i = 0; i < textLower.length && patternIndex < patternLower.length; i++) {
      if (textLower[i] === patternLower[patternIndex]) {
        patternIndex++
      }
    }
    return patternIndex === patternLower.length
  }

  const results = validGroups
    .map((group) => {
      const groupTitleMatch = fuzzyMatch(t(group.titleKey).toLowerCase(), query)

      return {
        ...group,
        items: group.items
          .filter((item) => {
            const labelMatch = fuzzyMatch(t(item.labelKey).toLowerCase(), query)
            const keywordMatch = item.keywords?.some((keyword) =>
              fuzzyMatch(keyword.toLowerCase(), query),
            )
            const categoryMatch = item.category
              ? fuzzyMatch(item.category.toLowerCase(), query)
              : false
            const groupMatch = groupTitleMatch
            const childMatch = item.children?.some((child) =>
              fuzzyMatch(t(child.labelKey).toLowerCase(), query),
            )
            return labelMatch || keywordMatch || categoryMatch || groupMatch || childMatch
          })
          .sort((a, b) => {
            // Prioritize exact matches
            const aExact = t(a.labelKey).toLowerCase().includes(query)
            const bExact = t(b.labelKey).toLowerCase().includes(query)
            if (aExact && !bExact) return -1
            if (!aExact && bExact) return 1
            return (a.priority || 999) - (b.priority || 999)
          }),
      }
    })
    .filter((group) => group.items.length > 0)
    .sort((a, b) => (a.priority || 999) - (b.priority || 999))

  return results
})

// Component-specific Methods
function handleDesktopToggle() {
  emit('toggle-sidebar')
}

function handleMobileClose() {
  emit('toggle-mobile-menu', false)
}

function handleNavigation(navigate: () => void, item: MenuItem) {
  navigate()
  addToRecent(item)

  // Toggle submenu if has children
  if (item.children && item.children.length > 0) {
    toggleItemExpansion(item.path)
  }

  // Close mobile menu on navigation
  if (props.isMobileMenuOpen) {
    handleMobileClose()
  }
}

function handleQuickAction(action: QuickAction) {
  action.action()
}

function handleUserAction(action: UserAction) {
  action.action()
  showUserMenu.value = false
}

function toggleUserMenu() {
  showUserMenu.value = !showUserMenu.value
}

function toggleGroupCollapse(groupIndex: number) {
  if (collapsedGroups.value.has(groupIndex)) {
    collapsedGroups.value.delete(groupIndex)
  } else {
    collapsedGroups.value.add(groupIndex)
  }
  persistence.saveCollapsedGroups(collapsedGroups.value)
}

function toggleItemExpansion(path: string) {
  if (expandedItems.value.has(path)) {
    expandedItems.value.delete(path)
  } else {
    expandedItems.value.add(path)
  }
  persistence.saveExpandedItems(expandedItems.value)
}

function toggleFavorite(item: MenuItem) {
  const index = favoriteItems.value.findIndex((fav) => fav.path === item.path)
  if (index > -1) {
    favoriteItems.value.splice(index, 1)
  } else {
    favoriteItems.value.push(item)
  }
  persistence.saveFavorites(favoriteItems.value)
}

function isFavorite(item: MenuItem): boolean {
  return favoriteItems.value.some((fav) => fav.path === item.path)
}

function addToRecent(item: MenuItem) {
  // Remove if already exists
  const index = recentItems.value.findIndex((recent) => recent.path === item.path)
  if (index > -1) {
    recentItems.value.splice(index, 1)
  }

  // Add to beginning
  recentItems.value.unshift(item)

  // Keep only last 3 items
  if (recentItems.value.length > 3) {
    recentItems.value = recentItems.value.slice(0, 3)
  }

  persistence.saveRecent(recentItems.value)
}

function removeFromRecent(item: MenuItem) {
  const index = recentItems.value.findIndex((recent) => recent.path === item.path)
  if (index > -1) {
    recentItems.value.splice(index, 1)
    persistence.saveRecent(recentItems.value)
  }
}

function clearRecent() {
  recentItems.value = []
  persistence.saveRecent(recentItems.value)
}

function clearSearch() {
  searchQuery.value = ''
  currentFocusIndex.value = -1
}

function focusFirstResult() {
  // Focus first menu item in search results
  nextTick(() => {
    const firstLink = sidebarRef.value?.querySelector('.sidebar-link') as HTMLElement
    firstLink?.focus()
  })
}

function handleKeyNavigation(event: KeyboardEvent) {
  if (!props.isSidebarOpen) return

  const links = Array.from(
    sidebarRef.value?.querySelectorAll('.sidebar-link, .submenu-link') || [],
  ) as HTMLElement[]

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      if (currentFocusIndex.value === -1) {
        currentFocusIndex.value = 0
      } else {
        currentFocusIndex.value = Math.min(currentFocusIndex.value + 1, links.length - 1)
      }
      links[currentFocusIndex.value]?.focus()
      links[currentFocusIndex.value]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      break
    case 'ArrowUp':
      event.preventDefault()
      if (currentFocusIndex.value === -1) {
        currentFocusIndex.value = links.length - 1
      } else {
        currentFocusIndex.value = Math.max(currentFocusIndex.value - 1, 0)
      }
      links[currentFocusIndex.value]?.focus()
      links[currentFocusIndex.value]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      break
    case 'ArrowRight': {
      event.preventDefault()
      const currentLink = links[currentFocusIndex.value]
      if (currentLink) {
        const expandButton = currentLink.parentElement?.querySelector(
          '[aria-label*="expand"]',
        ) as HTMLElement
        expandButton?.click()
      }
      break
    }
    case 'ArrowLeft': {
      event.preventDefault()
      const currentLink2 = links[currentFocusIndex.value]
      if (currentLink2) {
        const collapseButton = currentLink2.parentElement?.querySelector(
          '[aria-label*="collapse"]',
        ) as HTMLElement
        collapseButton?.click()
      }
      break
    }
    case '/':
      if (event.target !== searchInput.value) {
        event.preventDefault()
        searchInput.value?.focus()
      }
      break
    case 'Escape':
      if (searchQuery.value) {
        clearSearch()
      } else {
        searchInput.value?.blur()
      }
      break
    case 'Home':
      event.preventDefault()
      currentFocusIndex.value = 0
      links[0]?.focus()
      break
    case 'End':
      event.preventDefault()
      currentFocusIndex.value = links.length - 1
      links[links.length - 1]?.focus()
      break
  }
}

// Styling helper functions
function getLinkClasses(item: MenuItem, isActive: boolean, isExactActive: boolean) {
  const active = item.exact ? isExactActive : isActive
  return [
    'sidebar-link group relative flex items-center gap-3 px-2 py-2.5 rounded-md transition-all duration-200 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
    active
      ? 'bg-blue-600 text-white shadow-md'
      : 'text-gray-600 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-800 hover:text-blue-600 dark:hover:text-blue-400',
    !props.isSidebarOpen && 'justify-center px-2',
  ]
}

function getSubLinkClasses(item: MenuItem, isActive: boolean, isExactActive: boolean) {
  const active = item.exact ? isExactActive : isActive
  return [
    'submenu-link flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2',
    active
      ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-800 dark:text-teal-200 font-medium'
      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-200',
  ]
}

function getIconClasses(item: MenuItem, isActive: boolean, isExactActive: boolean) {
  const active = item.exact ? isExactActive : isActive
  return [
    'w-6 h-6 transition-all duration-200',
    props.isSidebarOpen && 'group-hover:scale-105',
    active && 'drop-shadow-sm',
  ]
}

function handleLogout() {
  // Implement logout logic
  console.log('Logout clicked')
  // router.push('/login')
}

// Theme toggle
const isDark = computed(() => themeStore.isDark)

function toggleTheme() {
  const newMode = themeStore.isDark ? 'light' : 'dark'
  themeStore.setMode(newMode)
}

// Load persisted state on mount
function loadPersistedState() {
  favoriteItems.value = persistence.loadFavorites()
  recentItems.value = persistence.loadRecent()
  collapsedGroups.value = persistence.loadCollapsedGroups()
  expandedItems.value = persistence.loadExpandedItems()
}

// Touch gesture handlers for mobile
function handleTouchStart(event: TouchEvent) {
  if (event.touches.length === 1 && event.touches[0]) {
    touchStartX.value = event.touches[0].clientX
    touchStartY.value = event.touches[0].clientY
  }
}

function handleTouchMove(event: TouchEvent) {
  // Prevent default to allow swipe detection
  if (event.touches.length === 1 && event.touches[0]) {
    const deltaX = event.touches[0].clientX - touchStartX.value
    const deltaY = event.touches[0].clientY - touchStartY.value

    // If horizontal swipe is more dominant than vertical, prevent scroll
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      event.preventDefault()
    }
  }
}

function handleTouchEnd(event: TouchEvent) {
  if (event.changedTouches.length === 1 && event.changedTouches[0]) {
    const deltaX = event.changedTouches[0].clientX - touchStartX.value
    const deltaY = event.changedTouches[0].clientY - touchStartY.value
    const absDeltaX = Math.abs(deltaX)
    const absDeltaY = Math.abs(deltaY)

    // Swipe right to open sidebar (on mobile)
    if (deltaX > 50 && absDeltaX > absDeltaY && !props.isMobileMenuOpen) {
      emit('toggle-mobile-menu', true)
    }
    // Swipe left to close sidebar (on mobile)
    else if (deltaX < -50 && absDeltaX > absDeltaY && props.isMobileMenuOpen) {
      emit('toggle-mobile-menu', false)
    }
  }
}

// Lazy loading for menu items
function setupLazyLoading() {
  if (!props.isSidebarOpen) return

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const groupId = entry.target.getAttribute('data-group-id')
          if (groupId) {
            visibleItems.value.add(groupId)
          }
        }
      })
    },
    {
      root: sidebarRef.value,
      rootMargin: '50px',
      threshold: 0.1,
    },
  )

  nextTick(() => {
    const groups = sidebarRef.value?.querySelectorAll('[data-group-id]')
    groups?.forEach((group) => observer.observe(group))
  })

  return () => {
    observer.disconnect()
  }
}

// Lifecycle
onMounted(() => {
  loadPersistedState()

  // Auto-expand current menu item's parent
  const currentItem = findMenuItemByPath(route.path, menuGroups)
  if (currentItem) {
    addToRecent(currentItem)
    // Auto-expand parent if has children
    const parentGroup = menuGroups.find((g) => g.items.some((i) => i.path === currentItem.path))
    if (parentGroup) {
      const parentItem = parentGroup.items.find(
        (i) => i.path === currentItem.path || i.children?.some((c) => c.path === currentItem.path),
      )
      if (parentItem && parentItem.children) {
        expandedItems.value.add(parentItem.path)
      }
    }
  }

  // Setup lazy loading
  if (props.isSidebarOpen) {
    setupLazyLoading()
  }
})

// Watch sidebar open state for lazy loading
watch(
  () => props.isSidebarOpen,
  (isOpen) => {
    if (isOpen) {
      nextTick(() => {
        setupLazyLoading()
      })
    } else {
      visibleItems.value.clear()
    }
  },
)

// Watch for route changes
watch(
  () => route.path,
  (newPath) => {
    const currentItem = findMenuItemByPath(newPath, menuGroups)
    if (currentItem) {
      addToRecent(currentItem)
    }
  },
)

// Close user menu when clicking outside
onMounted(() => {
  const handleClickOutside = (event: Event) => {
    if (
      showUserMenu.value &&
      sidebarRef.value &&
      !sidebarRef.value.contains(event.target as Node)
    ) {
      showUserMenu.value = false
    }
  }

  document.addEventListener('click', handleClickOutside)

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })
})
</script>
<style scoped>
/* Enhanced Overlay transition */
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

/* Enhanced Slide fade transition */
.slide-fade-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 1, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Enhanced Slide fade X transition */
.slide-fade-x-enter-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-x-leave-active {
  transition: all 0.15s cubic-bezier(0.4, 0, 1, 1);
}

.slide-fade-x-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}

.slide-fade-x-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

/* Enhanced Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Enhanced Slide right transition for expand button */
.slide-right-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.slide-right-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 1, 1);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-20px) scale(0.9);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(-10px) scale(0.95);
}

/* Enhanced Tooltip for collapsed sidebar */
.sidebar-link[data-tooltip]:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  left: calc(100% + 0.75rem);
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  z-index: 60;
  pointer-events: none;
  box-shadow:
    0 10px 25px -5px rgba(0, 0, 0, 0.3),
    0 8px 10px -6px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  animation: tooltipSlide 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  backdrop-filter: blur(8px);
}

.sidebar-link[data-tooltip]:hover::before {
  content: '';
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 6px solid transparent;
  border-right-color: #1f2937;
  z-index: 60;
  pointer-events: none;
  animation: tooltipSlide 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes tooltipSlide {
  from {
    opacity: 0;
    transform: translateY(-50%) translateX(-5px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) translateX(0) scale(1);
  }
}

/* Enhanced Custom scrollbar */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #cbd5e1 0%, #94a3b8 100%);
  border-radius: 3px;
  transition: background 0.2s ease;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #94a3b8 0%, #64748b 100%);
}

.dark .custom-scrollbar {
  scrollbar-color: #4b5563 transparent;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #4b5563 0%, #374151 100%);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #6b7280 0%, #4b5563 100%);
}

/* Enhanced Smooth hover effects */
.sidebar-link {
  position: relative;
  overflow: hidden;
}

.sidebar-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-link:hover::before {
  left: 100%;
}

/* Enhanced Focus styles for accessibility */
.sidebar-link:focus,
button:focus {
  outline: none;
  box-shadow:
    0 0 0 2px #14b8a6,
    0 0 0 4px rgba(255, 255, 255, 0.5);
}

.dark .sidebar-link:focus,
.dark button:focus {
  box-shadow:
    0 0 0 2px #14b8a6,
    0 0 0 4px rgba(31, 41, 55, 0.5);
}

/* Enhanced Badge animations */
@keyframes badgePulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

.sidebar-link .animate-pulse {
  animation: badgePulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Enhanced Loading skeleton */
@keyframes skeletonPulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: skeletonPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Enhanced Mobile gestures */
@media (max-width: 1024px) {
  .sidebar-link {
    min-height: 48px; /* Better touch targets for mobile */
    -webkit-tap-highlight-color: rgba(20, 184, 166, 0.2);
  }

  /* Swipe gesture indicator */
  .sidebar-link::after {
    content: '';
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 20px;
    background: linear-gradient(180deg, transparent, #14b8a6, transparent);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .sidebar-link:active::after {
    opacity: 1;
  }

  /* Better mobile scrolling */
  nav {
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
  }

  /* Larger touch targets for mobile */
  button,
  a {
    min-height: 44px;
    min-width: 44px;
  }
}

/* Enhanced Dark mode transitions */
* {
  transition-property:
    background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Enhanced Responsive design improvements */
@media (min-width: 768px) and (max-width: 1024px) {
  /* Tablet optimization */
  .sidebar-link {
    padding: 0.75rem;
    font-size: 0.875rem;
  }

  .sidebar-link svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  /* Better spacing for tablet */
  nav {
    padding: 1rem;
  }

  /* Optimize group titles for tablet */
  h3 {
    font-size: 0.7rem;
  }

  /* Better touch targets on tablet */
  button {
    min-height: 44px;
    min-width: 44px;
  }
}

/* Enhanced High contrast mode support */
@media (prefers-contrast: high) {
  .sidebar-link {
    border: 1px solid transparent;
  }

  .sidebar-link:hover,
  .sidebar-link:focus {
    border-color: currentColor;
  }
}

/* Enhanced Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Enhanced Print styles */
@media print {
  aside {
    display: none;
  }
}

/* Enhanced Performance optimizations */
.sidebar-link,
.sidebar-link svg {
  will-change: transform;
}

.sidebar-link:hover svg {
  transform: scale(1.1);
}

/* Enhanced Gradient backgrounds */
.bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}

.from-teal-500 {
  --tw-gradient-from: #14b8a6;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(20, 184, 166, 0));
}

.to-teal-600 {
  --tw-gradient-to: #0d9488;
}

/* Enhanced Shadow effects */
.shadow-teal-500\/50 {
  box-shadow:
    0 10px 15px -3px rgba(20, 184, 166, 0.5),
    0 4px 6px -2px rgba(20, 184, 166, 0.5);
}

/* Enhanced Backdrop blur */
.backdrop-blur-sm {
  backdrop-filter: blur(4px);
}

/* Enhanced Ring utilities */
.ring-2 {
  box-shadow: 0 0 0 2px var(--tw-ring-color);
}

.ring-teal-500 {
  --tw-ring-color: #14b8a6;
}

.focus\:ring-2:focus {
  box-shadow: 0 0 0 2px var(--tw-ring-color);
}

.focus\:ring-teal-500:focus {
  --tw-ring-color: #14b8a6;
}
</style>

